import React, { createContext, useContext, useEffect, useState } from "react";
import type { DarkModeAvailability } from "../hooks/useDarkModeAvailability";

// Follows shadcn's Vite dark-mode ThemeProvider. index.html applies the same
// class before first paint from THEME_STORAGE_KEY, so keep the two in sync.
export type Theme = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";

export const THEME_STORAGE_KEY = "midship-ui-theme";

const isTheme = (value: string | null): value is Theme =>
  value === "dark" || value === "light" || value === "system";

const readStoredTheme = (fallback: Theme): Theme => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
};

const hasStoredTheme = (): boolean => {
  try {
    return isTheme(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return false;
  }
};

const systemPrefersDark = () =>
  window.matchMedia("(prefers-color-scheme: dark)").matches;

type ThemeContextValue = {
  /** Whether the `dark-mode` flag offers the theme picker. */
  available: boolean;
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  available: false,
  theme: "light",
  resolvedTheme: "light",
  setTheme: () => {},
});

export const ThemeProvider = ({
  children,
  availability,
  defaultTheme = "light",
}: {
  children: React.ReactNode;
  availability: DarkModeAvailability;
  defaultTheme?: Theme;
}) => {
  const [theme, setThemeState] = useState<Theme>(() =>
    readStoredTheme(defaultTheme),
  );
  const [systemIsDark, setSystemIsDark] = useState(systemPrefersDark);

  // With the picker on, first-time users default to "system". Storing it lets
  // index.html paint the right theme on the next load. With it off, a stored
  // "system" is cleared so index.html doesn't paint dark before React forces
  // light; nothing is lost, as "system" is the default once the flag is back.
  useEffect(() => {
    if (availability === "off") {
      try {
        if (localStorage.getItem(THEME_STORAGE_KEY) === "system")
          localStorage.removeItem(THEME_STORAGE_KEY);
      } catch {
        // Storage blocked: nothing was stored to paint from.
      }
      return;
    }
    if (availability !== "on" || hasStoredTheme()) return;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, "system");
    } catch {
      // Storage blocked: "system" still applies for this session.
    }
    setThemeState("system");
  }, [availability]);

  // Unlike the shadcn reference, "system" tracks OS changes live.
  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemIsDark(e.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  // While the flag is pending the stored choice (already applied by
  // index.html) stands; with the flag off everyone gets light.
  const resolvedTheme: ResolvedTheme =
    availability === "off"
      ? "light"
      : theme === "system"
        ? systemIsDark
          ? "dark"
          : "light"
        : theme;

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = (next: Theme) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Storage blocked: the choice still applies for this session.
    }
    setThemeState(next);
  };

  return (
    <ThemeContext.Provider
      value={{
        available: availability === "on",
        theme,
        resolvedTheme,
        setTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => useContext(ThemeContext);
